import { z } from 'zod';
import { SessionPendingHourInfoSchema } from './session-hour.js';
// Deposits / señal — the types backing partial-payment bookings (deposit now,
// balance later) and the general-purpose payment-request link. See
// docs/NUBERU_DEPOSITS_DATA_MODEL.md for the full design.
//
// Every enum is a `const` tuple so the Drizzle pgEnum and the Zod schema stay
// in lockstep (same pattern as BOOKING_STATES / PAYMENT_PROCESSORS). Money is
// always integer cents; percentages are always integers (float percentage
// arithmetic on money produces off-by-one-cent refund errors).
// --- Enums ---------------------------------------------------------------
// Deliberately a single-value enum: v1 supports percentage deposits only. The
// tuple is left open so 'fixed' can be added later without a shape change.
export const ACTIVITY_DEPOSIT_TYPES = ['percentage'];
export const ActivityDepositTypeSchema = z.enum(ACTIVITY_DEPOSIT_TYPES);
// How the outstanding balance is collected after the deposit is taken:
// 'automatic' = off-session Stripe charge on the card on file at balance-due
// time; 'manual' = the operator collects in person and records it.
export const BALANCE_COLLECTION_METHODS = ['automatic', 'manual'];
export const BalanceCollectionMethodSchema = z.enum(BALANCE_COLLECTION_METHODS);
// What a `booking_payments` ledger row represents. 'full' is a non-deposit
// online payment made through a payment request (e.g. a phone booking paid
// retroactively) — the ledger row for what used to be the single PI.
// 'write_off' (baja) is NOT money that moved: it clears an outstanding balance
// deliberately (courtesy, no-show, uncollectable, rounding, or the closed-off
// remainder of an under-collection). Write-off rows reduce the derived balance
// but are excluded from collected-cash (revenue) — see COLLECTED_CENTS_SQL.
export const BOOKING_PAYMENT_KINDS = ['deposit', 'balance', 'full', 'write_off'];
export const BookingPaymentKindSchema = z.enum(BOOKING_PAYMENT_KINDS);
// Why a ledger row's amount is what it is. Null on an exact-amount payment
// (the collected amount matched the outstanding balance). Present when:
//   - a payment differs from the balance (partial or over-collection), OR
//   - the row is a 'write_off' baja.
// The reason drives Reportes aggregation (§9) — a €200/mo courtesy habit must
// be distinguishable from a €200/mo collection problem.
//
//   Under-collection (cobra de menos):
//     resto        — customer pays the rest later; the balance stays open
//                    (recorded on the partial PAYMENT row, no write-off)
//     descuento    — the shortfall is written off as a discount
//     menos_gente  — fewer people came than booked; written off as an adjustment
//     precio_mal   — the price was wrong; written off as a correction
//   Over-collection (cobra de más), recorded on the PAYMENT row:
//     extras       — material / extras not itemised
//     mas_gente    — more people came than booked
//     (precio_mal also applies here — the price was wrong the other way)
//   Write-off (No cobrar), recorded on the WRITE_OFF row:
//     cortesia     — on the house
//     no_show      — no-show with an outstanding balance and no retained deposit
//     no_cobrable  — could not be collected
//     error_reserva— booking mistake
//   Automatic:
//     redondeo     — a sub-threshold residue auto-closed so the counter reaches
//                    zero without rounding drift (see ROUNDING_WRITE_OFF_MAX_CENTS)
export const BOOKING_PAYMENT_REASONS = [
    'resto',
    'descuento',
    'menos_gente',
    'precio_mal',
    'extras',
    'mas_gente',
    'cortesia',
    'no_show',
    'no_cobrable',
    'error_reserva',
    'redondeo',
];
export const BookingPaymentReasonSchema = z.enum(BOOKING_PAYMENT_REASONS);
// Reasons that a write-off (baja) row can carry — the subset the operator picks
// in "No cobrar", plus the automatic 'redondeo', plus the under-collection
// shortfall reasons ('descuento' | 'menos_gente' | 'precio_mal') that close the
// difference when a partial payment is recorded for less than the balance.
export const WRITE_OFF_REASONS = ['cortesia', 'no_show', 'no_cobrable', 'error_reserva'];
export const WriteOffReasonSchema = z.enum(WRITE_OFF_REASONS);
// A sub-threshold outstanding residue (e.g. a 3-cent rounding tail) is
// auto-closed as a 'redondeo' write-off rather than left to keep the "por
// cobrar" counter permanently non-zero. `[INFERIDO]` — confirm the threshold.
export const ROUNDING_WRITE_OFF_MAX_CENTS = 50;
// How a ledger row's money was collected: 'online' (Stripe PI) or 'manual'
// (cash / card reader recorded by staff).
export const BOOKING_PAYMENT_COLLECTION_METHODS = ['online', 'manual'];
export const BookingPaymentCollectionMethodSchema = z.enum(BOOKING_PAYMENT_COLLECTION_METHODS);
export const BOOKING_PAYMENT_STATUSES = [
    'pending',
    'succeeded',
    'failed',
    'refunded',
    'partially_refunded',
];
export const BookingPaymentStatusSchema = z.enum(BOOKING_PAYMENT_STATUSES);
// The balance-tracking state on `bookings.balance_state`. Additive to (never a
// replacement for) `payment_state`: 'none' for every booking that predates this
// feature or never had a deposit, so existing `payment_state` reads are
// untouched. 'owed' → 'paid' (settled) or 'failed' (automatic charge declined /
// needs 3DS, fell back to a payment request).
export const BOOKING_BALANCE_STATES = ['none', 'owed', 'paid', 'failed'];
export const BookingBalanceStateSchema = z.enum(BOOKING_BALANCE_STATES);
// Why a payment request was raised. 'custom' is an arbitrary amount against a
// booking (operator's discretion); the others target a known outstanding piece.
// What the money is for. The first four are all parts of a BOOKING's price;
// 'bono' is the odd one out and is the discriminator that says so — a request
// carrying it is attached to a pack, not to a booking, and settles by
// activating that pack rather than by writing a booking ledger row. 'rental' is
// the same kind of discriminator: attached to a rental, settled by writing a
// `rental_payments` charge with method `link`.
export const PAYMENT_REQUEST_REASONS = [
    'deposit',
    'balance',
    'full',
    'custom',
    'bono',
    'rental',
];
export const PaymentRequestReasonSchema = z.enum(PAYMENT_REQUEST_REASONS);
export const PAYMENT_REQUEST_STATUSES = ['pending', 'paid', 'expired', 'cancelled'];
export const PaymentRequestStatusSchema = z.enum(PAYMENT_REQUEST_STATUSES);
// --- Payment request DTOs (solicitud de pago) ----------------------------
// Operator body to raise a payment request against a booking. `amountCents`
// defaults to the booking's current outstanding amount; requesting LESS than
// outstanding is admin-only (enforced server-side). `expiresAt` defaults to 7
// days out when omitted.
export const CreatePaymentRequestRequestSchema = z.object({
    reason: PaymentRequestReasonSchema,
    amountCents: z.number().int().positive().optional(),
    expiresAt: z.string().datetime({ offset: true }).optional(),
});
// Operator-facing projection of a payment_requests row. Includes the opaque
// `token` (the dashboard renders the copy-link URL from it) — safe because this
// projection is only ever returned on authenticated, operator-scoped routes.
export const PaymentRequestSchema = z.object({
    id: z.string().uuid(),
    // Exactly ONE subject: a booking, a pack or a rental, never two and never
    // neither (enforced by a CHECK constraint, not by convention). A request is a
    // demand for money against something the customer bought.
    bookingId: z.string().uuid().nullable(),
    bonoId: z.string().uuid().nullable(),
    // Absent on rows from before rentals had pay-links; the API always sends it.
    rentalBookingId: z.string().uuid().nullable().optional(),
    amountCents: z.number().int().positive(),
    currency: z.literal('EUR'),
    reason: PaymentRequestReasonSchema,
    status: PaymentRequestStatusSchema,
    token: z.string().min(1),
    expiresAt: z.string(),
    createdAt: z.string(),
    // When the pay link was last EMAILED to the customer (null = created but
    // never sent). Distinct from `createdAt` because the automatic-balance-charge
    // fallback creates a request silently at decline time; only an explicit send
    // puts the link in the customer's hands. Drives the Avisos inbox suppression
    // window — see services/avisos.ts.
    sentAt: z.string().nullable(),
    paidAt: z.string().nullable(),
});
export const PaymentRequestResponseSchema = z.object({ data: PaymentRequestSchema });
export const PaymentRequestListResponseSchema = z.object({ data: z.array(PaymentRequestSchema) });
// Customer-safe summary returned by the public token lookup — no ids, no token
// echoed back, just what the pay page needs to render.
export const PublicPaymentRequestSummarySchema = z.object({
    operatorName: z.string(),
    activityName: z.string().nullable(),
    sessionStartsAt: z.string().nullable(),
    // The session's hour is still pending: `sessionStartsAt` is provisional and
    // the page prints the part of the day instead of a time (handover Y).
    sessionPendingHour: SessionPendingHourInfoSchema.nullable(),
    amountCents: z.number().int().positive(),
    currency: z.literal('EUR'),
    reason: PaymentRequestReasonSchema,
    status: PaymentRequestStatusSchema,
    // Whether the link can take a card now: the operator has a connected gateway.
    // Without one (never connected, or disconnected since the link was sent) the
    // page offers no "Pagar" and shows how the operator is paid instead
    // (handover W, W9).
    cardPayment: z.boolean(),
    // The operator's "Cómo te pagan" line, in the page's language; null when
    // they have not written one.
    paymentInstructions: z.string().nullable(),
    // Handover I §5 (board T-Pagar): for a booking, its whole price and what is
    // already paid, so the page can show Total · Pagado · Queda; and whether the
    // rest can also be paid on the day. Null for a bono. Optional so older
    // fixtures keep compiling.
    booking: z
        .object({
        totalCents: z.number().int().nonnegative(),
        paidCents: z.number().int().nonnegative(),
        reference: z.string().nullable(),
        formatLabel: z.string().nullable(),
        payableOnTheDay: z.boolean(),
    })
        .nullable()
        .optional(),
});
export const PublicPaymentRequestSummaryResponseSchema = z.object({
    data: PublicPaymentRequestSummarySchema,
});
// One row of a booking's payment ledger, operator-facing (dashboard booking
// detail). Shows what money moved and its state.
export const BookingPaymentEntrySchema = z.object({
    id: z.string().uuid(),
    kind: BookingPaymentKindSchema,
    collectionMethod: BookingPaymentCollectionMethodSchema,
    amountCents: z.number().int().nonnegative(),
    currency: z.literal('EUR'),
    status: BookingPaymentStatusSchema,
    // Null on an exact-amount payment; set when the amount differs from the
    // balance, or on a 'write_off' baja. See BOOKING_PAYMENT_REASONS.
    reason: BookingPaymentReasonSchema.nullable(),
    paymentIntentId: z.string().nullable(),
    refundAmountCents: z.number().int().nonnegative().nullable(),
    notes: z.string().nullable(),
    chargedAt: z.string().nullable(),
    refundedAt: z.string().nullable(),
    createdAt: z.string(),
});
// The full ledger for a booking: the payment rows + any payment requests raised
// against it. Returned by GET /operators/:id/bookings/:bid/payments.
export const BookingLedgerSchema = z.object({
    payments: z.array(BookingPaymentEntrySchema),
    paymentRequests: z.array(PaymentRequestSchema),
});
export const BookingLedgerResponseSchema = z.object({ data: BookingLedgerSchema });
// Poll result for the public pay page, mirroring BookingIntentResult.
export const PaymentRequestResultSchema = z.object({
    status: PaymentRequestStatusSchema,
});
// --- Policies (stored as jsonb on `operators`, validated before write) ----
// Operator-level deposit policy. Stored on `operators.deposit_policy`; null
// there means deposits are disabled for the operator. `depositValue` is a
// percentage (1-100) of the chargeable total. `balanceDueOffsetHours` is how
// many hours before the session the automatic balance charge fires — only
// meaningful when `balanceCollectionMethod === 'automatic'`.
export const DepositPolicySchema = z.object({
    enabled: z.boolean(),
    depositType: ActivityDepositTypeSchema,
    depositValue: z.number().int().min(1).max(100),
    balanceCollectionMethod: BalanceCollectionMethodSchema,
    balanceDueOffsetHours: z.number().int().nonnegative(),
});
// NOTE: the separate deposit refund policy (`DepositRefundPolicy` + tiers) was
// retired 2026-07-14 — refunds are unified under the cancellation policy §6
// (`refund = min(policyPct × total, amountCollected)`). See `cancellation.ts`.
// Snapshot of the deposit split persisted on a `booking_intents` staging row at
// payment initiation, so the webhook fulfilment builds the booking + ledger
// from what the customer was actually charged rather than recomputing the split
// (the full total is still re-verified — see the CHARGE_MISMATCH guard). Null on
// the staging row means "no deposit — full charge", the unchanged path.
export const BookingIntentDepositPlanSchema = z.object({
    // What the PI captured now (equals the staging row's amountCents).
    depositAmountCents: z.number().int().nonnegative(),
    // What remains owed after the deposit.
    balanceDueCents: z.number().int().nonnegative(),
    balanceCollectionMethod: BalanceCollectionMethodSchema,
    // Hours before session start the automatic balance charge should fire.
    // Meaningless (and unused) for manual collection.
    balanceDueOffsetHours: z.number().int().nonnegative(),
    // The Stripe Customer created at initiate when the card is saved for an
    // automatic off-session balance charge; null for manual collection.
    stripeCustomerId: z.string().min(1).nullable(),
});
// --- Effective (resolved) policy the widget consumes ---------------------
// The resolved policy for a specific activity type: activity override merged
// over the operator default, collapsed to disabled when neither opts in. The
// widget and dashboard read this pre-resolved shape — they never re-implement
// the inheritance rule. `balanceDueOffsetHours` travels so the widget can tell
// the customer the day of an automatic second charge (session start − offset,
// as the server schedules it); it means nothing for manual collection.
export const OperatorDepositPolicyViewSchema = z.object({
    // null = the operator has never configured deposits (disabled globally).
    policy: DepositPolicySchema.nullable(),
});
export const OperatorDepositPolicyResponseSchema = z.object({
    data: OperatorDepositPolicyViewSchema,
});
export const EffectiveDepositPolicySchema = z.discriminatedUnion('enabled', [
    z.object({ enabled: z.literal(false) }),
    z.object({
        enabled: z.literal(true),
        depositType: ActivityDepositTypeSchema,
        depositValue: z.number().int().min(1).max(100),
        balanceCollectionMethod: BalanceCollectionMethodSchema,
        balanceDueOffsetHours: z.number().int().nonnegative(),
    }),
]);
// --- How a booking is charged: one choice (handover E) -----------------------
//
// The four ways an operator with a card gateway can charge a booking, as ONE
// choice. Underneath it is still the deposit policy (`enabled` +
// `balanceCollectionMethod`) plus a "charge nothing at booking" flag; this enum
// is the resolved projection the screens and the widget speak:
//   full              — todo al reservar (deposit disabled)
//   deposit_manual    — señal now, the rest in person
//   deposit_automatic — señal now, the rest charged off the saved card (Stripe)
//   none              — nada al reservar: the booking is confirmed por pagar
export const CHARGE_MODES = ['full', 'deposit_manual', 'deposit_automatic', 'none'];
export const ChargeModeSchema = z.enum(CHARGE_MODES);
// The payment deadline for a booking made without paying (por pagar).
//   off            — nothing is ever cancelled for not paying
//   after_booking  — cancel N hours after booking (never later than the start)
//   before_session — cancel N hours before the session; a booking made with
//                    less margin than that has no deadline (pays on arrival)
export const PAYMENT_DEADLINE_MODES = ['off', 'after_booking', 'before_session'];
export const PaymentDeadlineModeSchema = z.enum(PAYMENT_DEADLINE_MODES);
// 30 days: past that a "deadline" stops meaning anything for a session booking.
export const PAYMENT_DEADLINE_MAX_HOURS = 720;
export const PaymentDeadlineHoursSchema = z.number().int().min(1).max(PAYMENT_DEADLINE_MAX_HOURS);
export const PaymentDeadlineRuleSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('off') }),
    z.object({ mode: z.literal('after_booking'), hours: PaymentDeadlineHoursSchema }),
    z.object({ mode: z.literal('before_session'), hours: PaymentDeadlineHoursSchema }),
]);
// The operator's own line telling customers how to pay them when nothing is
// charged online: "Bizum al 684 120 337 o en mano".
export const PAYMENT_INSTRUCTIONS_MAX = 140;
export const PaymentInstructionsSchema = z.string().trim().min(1).max(PAYMENT_INSTRUCTIONS_MAX);
// Ajustes › Cobros. One document for the whole screen: the card choice with its
// señal percentage and offset, and the block "Cuando te pagan en mano o por
// Bizum". Without a gateway the card choice is not asked; a señal is then
// `deposit_manual` (pedir una señal) or `full` (no señal), owed off-line.
export const OperatorChargeSettingsSchema = z.object({
    chargeMode: ChargeModeSchema,
    depositPercent: z.number().int().min(1).max(100),
    balanceDueOffsetHours: z.number().int().min(0).max(PAYMENT_DEADLINE_MAX_HOURS),
    paymentInstructions: PaymentInstructionsSchema.nullable(),
    paymentDeadline: PaymentDeadlineRuleSchema,
});
export const UpdateOperatorChargeSettingsRequestSchema = OperatorChargeSettingsSchema;
export const OperatorChargeSettingsViewSchema = OperatorChargeSettingsSchema.extend({
    // Which gateway is connected, so the screen knows whether to ask the card
    // choice at all and whether "se cobra solo" is available.
    activeProcessor: z.enum(['stripe', 'redsys']).nullable(),
    // Open bookings that already have a payment deadline. They keep it whatever
    // the operator changes here, and the screen says so (handover W, W17).
    openDeadlineCount: z.number().int().nonnegative(),
});
export const OperatorChargeSettingsResponseSchema = z.object({
    data: OperatorChargeSettingsViewSchema,
});
// What the widget is told about the deadline of a booking made without paying,
// resolved for one activity:
//   none            — the operator has no deadline: no sentence about cancelling
//   pay_on_arrival  — this activity has "Sin plazo": the customer can pay on arrival
//   after_booking / before_session — the rule; the widget computes the moment
//                     for display, the server stores the authoritative one
export const PublicPaymentDeadlineSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('pay_on_arrival') }),
    z.object({ kind: z.literal('after_booking'), hours: PaymentDeadlineHoursSchema }),
    z.object({ kind: z.literal('before_session'), hours: PaymentDeadlineHoursSchema }),
]);
// Operator action "Dar más tiempo": moves one booking's deadline by 24 hours,
// never past the session's start.
export const PAYMENT_DEADLINE_EXTENSION_HOURS = 24;
// POST /operators/:operatorId/bookings/:bookingId/extend-deadline
export const ExtendPaymentDeadlineResponseSchema = z.object({
    data: z.object({ paymentDeadlineAt: z.string().datetime({ offset: true }) }),
});
const HOUR_MS = 60 * 60 * 1000;
export const computePaymentDeadline = (rule, bookedAt, startsAt) => {
    switch (rule.kind) {
        case 'none':
            return { kind: 'none' };
        case 'pay_on_arrival':
            return { kind: 'on_arrival', because: 'activity' };
        case 'after_booking': {
            const at = Math.min(bookedAt.getTime() + rule.hours * HOUR_MS, startsAt.getTime());
            return { kind: 'at', at: new Date(at) };
        }
        case 'before_session': {
            const at = startsAt.getTime() - rule.hours * HOUR_MS;
            if (at <= bookedAt.getTime())
                return { kind: 'on_arrival', because: 'margin' };
            return { kind: 'at', at: new Date(at) };
        }
    }
};
// The operator rule merged with the activity's override. `activityMode` null =
// "El de Cobros"; 'off' = "Sin plazo en esta actividad", which is the activity's
// "se puede pagar al llegar". An activity override with a mode but no hours is
// incomplete and falls back to the operator's rule rather than inventing hours.
export const resolvePublicPaymentDeadline = (operatorRule, activityMode, activityHours) => {
    if (activityMode === 'off')
        return { kind: 'pay_on_arrival' };
    if (activityMode !== null && activityHours !== null) {
        return { kind: activityMode, hours: activityHours };
    }
    if (operatorRule.mode === 'off')
        return { kind: 'none' };
    return { kind: operatorRule.mode, hours: operatorRule.hours };
};
// --- Rendering a policy as a sentence ----------------------------------------
//
// Moved here from the dashboard when the API needed the SAME sentence: a
// settings change writes what the deposit rule was and what it became into the
// dietario, and a log that phrases it differently from the screen the operator
// just left is one they have to translate. Mirrors the cancellation policy's
// `formatCancellationSentence`.
// e.g. "Señal del 30 % · el resto se cobra 24 h antes de la sesión".
export const formatDepositSentence = (policy) => {
    if (!policy.enabled)
        return 'No se pide señal por adelantado';
    const tail = policy.balanceCollectionMethod === 'automatic'
        ? `el resto se cobra ${policy.balanceDueOffsetHours} h antes de la sesión`
        : 'el resto se cobra al llegar';
    return `Señal del ${policy.depositValue} % · ${tail}`;
};
//# sourceMappingURL=deposits.js.map